import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import useVideoEnabled from '../hooks/useVideoEnabled';
import VideoService from '../services/videoService';
import { uploadVideo, resumeVideo } from '../services/videoUploadClient';
import {
  rememberUpload, rememberedUpload, forgetUpload, fileFingerprint, matchesPausedUpload, pausedProgress,
  touchUpload, isLikelyActive
} from '../utils/videoResume';

/**
 * Every video upload in the app, in one queue (VIDEO_PLAN V5). It lives at
 * the app's root, so an upload keeps going when the sheet or page that
 * started it closes; only closing the tab stops it (and the browser warns
 * first). The Recap composer, the visit sheet and the review form all read
 * their own rows from here (useVideoUploads).
 *
 * One at a time: each clip can be gigabytes, and the server allows two open
 * uploads per person.
 *
 * Rows: { localId, token, name, phase, sentBytes, totalBytes, etaSeconds,
 * code, context, source, expiresAt }. Phases:
 *   waiting | preparing | uploading | finishing — in progress;
 *   interrupted — the upload is open and this tab still holds the file:
 *     Resume carries on (a lost connection, a part that wouldn't go);
 *   paused — open on the server from an earlier visit (the tab closed): the
 *     file must be picked again (resume(localId, file));
 *   elsewhere — open and still moving (a part in the last five minutes, or
 *     another tab's heartbeat): someone is sending it from another tab or
 *     device, so it offers nothing — a Cancel here would kill that upload.
 *     Paused and elsewhere rows are re-checked every minute (and a paused
 *     one again just before its Cancel): one that starts moving turns
 *     elsewhere, one that goes quiet turns paused, and one that finished or
 *     closed where it was being sent leaves — a Cancel there would DELETE
 *     the finished clip (the route deletes clips too, V4);
 *   failed — refused or closed; Dismiss removes it.
 * Cancel/Dismiss deletes an open upload: the slot and the minutes come back.
 *
 * A finished upload is announced to subscribers ({ type: 'finished', token,
 * context, source }) so a surface can re-read its clips.
 */

const VideoUploadsContext = createContext(null);
const ACTIVE = ['waiting', 'preparing', 'uploading', 'finishing'];
const HEARTBEAT_MS = 15 * 1000;
const RECHECK_MS = 60 * 1000;

const contextOf = (u) => ({
  trip_id: u.trip_id ?? null,
  album_id: u.album_id ?? null,
  activity_id: u.activity_id ?? null,
  trip_day_id: u.trip_day_id ?? null,
  attraction_id: u.attraction_id ?? null
});
const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function VideoUploadsProvider({ children }) {
  const enabled = useVideoEnabled();
  const { currentUser } = useAuth();
  const userId = currentUser?.id || null;

  const [rows, setRows] = useState([]);
  const [quota, setQuota] = useState(null);
  // Kept in step by updateRows (every row change goes through it), never
  // copied from state after a render: that copy could land after a newer change.
  const rowsRef = useRef(rows);
  const filesRef = useRef(new Map()); // localId → File (never in state)
  const controllersRef = useRef(new Map());
  const queueRef = useRef(Promise.resolve());
  const listenersRef = useRef(new Set());
  // Bumped when the signed-in person changes: a late answer for the last
  // person's upload must not land in the next person's rows.
  const generationRef = useRef(0);

  // Every change goes to rowsRef at once as well as to state: code that reads
  // rowsRef after an await (recheck, cancel) must see a Resume or a token that
  // was set a moment ago, not the last committed render.
  const updateRows = useCallback((fn) => {
    rowsRef.current = fn(rowsRef.current);
    setRows(fn);
  }, []);
  const patch = useCallback((localId, changes) => {
    updateRows(prev => prev.map(r => (r.localId === localId ? { ...r, ...changes } : r)));
  }, [updateRows]);
  const removeRow = useCallback((localId) => {
    filesRef.current.delete(localId);
    updateRows(prev => prev.filter(r => r.localId !== localId));
  }, [updateRows]);
  // Tokens this tab created: the first listing of open uploads must not add a
  // paused row for one of them whose create answer is still on its way.
  const ownTokensRef = useRef(new Set());

  const refreshQuota = useCallback(async () => {
    if (!enabled || !userId) return;
    const generation = generationRef.current;
    const res = await VideoService.getQuota();
    if (res.success && generation === generationRef.current) setQuota(res.quota);
  }, [enabled, userId]);

  // A new person (or none): drop the last one's rows, stop their uploads.
  useEffect(() => {
    generationRef.current += 1;
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current.clear();
    filesRef.current.clear();
    ownTokensRef.current.clear();
    rowsRef.current = [];
    setRows([]);
    setQuota(null);
  }, [userId]);

  // Uploads left open by an earlier visit (the tab closed mid-upload): shown
  // as paused rows where they belong, to be finished by picking the file again.
  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let cancelled = false;
    const generation = generationRef.current;
    (async () => {
      const res = await VideoService.listOpenUploads();
      if (cancelled || !res.success || generation !== generationRef.current) return;
      const found = [];
      for (const upload of res.uploads || []) {
        if (ownTokensRef.current.has(upload.token) || rowsRef.current.some(r => r.token === upload.token)) continue;
        const status = await VideoService.getUpload(upload.token);
        if (cancelled || generation !== generationRef.current) return;
        const { sentBytes } = pausedProgress(upload, status.success ? status.uploaded_parts : []);
        const remembered = rememberedUpload(upload.token);
        found.push({
          localId: `paused-${upload.token}`,
          token: upload.token,
          name: remembered?.name || null,
          phase: isLikelyActive({ idleMs: status.idle_ms, remembered }) ? 'elsewhere' : 'paused',
          sentBytes,
          totalBytes: upload.declared_bytes,
          etaSeconds: null,
          code: null,
          context: contextOf(upload),
          // Where it was started, if on this device (the Recap composer picks
          // a clip it started into the recap once it's encoded).
          source: remembered?.source || null,
          expiresAt: upload.expires_at || null
        });
      }
      if (found.length) {
        updateRows(prev => [...prev, ...found.filter(f => !ownTokensRef.current.has(f.token) && !prev.some(r => r.token === f.token))]);
      }
    })();
    refreshQuota();
    return () => { cancelled = true; };
  }, [enabled, userId, refreshQuota, updateRows]);

  // This tab's uploads beat in localStorage, so another tab in this browser
  // shows them as "elsewhere" rather than offering to cancel them.
  const sendingTokens = rows.filter(r => r.token && ACTIVE.includes(r.phase)).map(r => r.token).join(',');
  useEffect(() => {
    if (!sendingTokens) return undefined;
    const beat = () => sendingTokens.split(',').forEach(touchUpload);
    beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [sendingTokens]);

  // Look again at an open upload this tab isn't sending: is someone sending
  // it now, has it gone quiet, or did it finish (or close) where it was sent?
  // → 'elsewhere' | 'paused' | 'gone' | null (couldn't tell).
  const recheck = useCallback(async (localId) => {
    const before = rowsRef.current.find(r => r.localId === localId);
    if (!before?.token) return null;
    const generation = generationRef.current;
    const status = await VideoService.getUpload(before.token);
    if (generation !== generationRef.current) return null;
    const row = rowsRef.current.find(r => r.localId === localId);
    if (!row || !['paused', 'elsewhere'].includes(row.phase)) return null;
    if (status.success && status.video?.processing_status === 'uploading') {
      const { sentBytes } = pausedProgress(status.video, status.uploaded_parts);
      const phase = isLikelyActive({ idleMs: status.idle_ms, remembered: rememberedUpload(row.token) }) ? 'elsewhere' : 'paused';
      // Only while it is still waiting: a Resume since the look began owns the row.
      updateRows(prev => prev.map(r => (
        r.localId === localId && ['paused', 'elsewhere'].includes(r.phase) ? { ...r, phase, sentBytes } : r
      )));
      return phase;
    }
    if (status.success || status.code === 'not_found') {
      forgetUpload(row.token);
      removeRow(localId);
      return 'gone';
    }
    return null;
  }, [updateRows, removeRow]);

  // Every minute, for the open uploads this tab isn't sending.
  const watchedIds = rows.filter(r => r.token && ['paused', 'elsewhere'].includes(r.phase)).map(r => r.localId).join(',');
  useEffect(() => {
    if (!watchedIds) return undefined;
    const timer = setInterval(async () => {
      for (const localId of watchedIds.split(',')) await recheck(localId);
    }, RECHECK_MS);
    return () => clearInterval(timer);
  }, [watchedIds, recheck]);

  // Closing the tab mid-upload: the browser asks first.
  const busy = rows.some(r => ACTIVE.includes(r.phase));
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  // Run one upload task in the queue. `task(signal, onProgress)` → client result.
  const enqueue = useCallback((localId, task) => {
    const controller = new AbortController();
    controllersRef.current.set(localId, controller);
    const generation = generationRef.current;
    queueRef.current = queueRef.current.then(async () => {
      if (controller.signal.aborted || generation !== generationRef.current) {
        controllersRef.current.delete(localId);
        return;
      }
      const onProgress = (p) => { if (generation === generationRef.current) patch(localId, p); };
      const result = await task(controller.signal, onProgress);
      controllersRef.current.delete(localId);
      if (generation !== generationRef.current) return;
      const row = rowsRef.current.find(r => r.localId === localId);
      if (result.success) {
        const token = result.video?.token || row?.token || null;
        if (token) forgetUpload(token);
        removeRow(localId);
        const event = { type: 'finished', token, context: row?.context || null, source: row?.source || null };
        for (const listener of listenersRef.current) listener(event);
        refreshQuota();
      } else if (result.code === 'cancelled') {
        removeRow(localId);
        refreshQuota();
      } else {
        const token = result.token || row?.token || null;
        if (!result.resumable && token) forgetUpload(token);
        patch(localId, {
          phase: result.resumable ? 'interrupted' : 'failed',
          code: result.code || 'upload_failed',
          token,
          etaSeconds: null,
          // What R2 really holds (progress events run ahead of a failed part).
          ...(Number.isFinite(result.sentBytes) ? { sentBytes: result.sentBytes } : {})
        });
        refreshQuota();
      }
    });
  }, [patch, removeRow, refreshQuota]);

  /** Upload these files with this context (trip_id, activity_id, …). */
  const start = useCallback((files, context, { source = null } = {}) => {
    for (const file of files) {
      const localId = newId();
      filesRef.current.set(localId, file);
      updateRows(prev => [...prev, {
        localId, token: null, name: file.name, phase: 'waiting', sentBytes: 0, totalBytes: file.size,
        etaSeconds: null, code: null, context: { ...contextOf(context || {}) }, source, expiresAt: null
      }]);
      enqueue(localId, (signal, onProgress) => uploadVideo(file, context, {
        signal,
        onProgress,
        onCreated: (video) => {
          ownTokensRef.current.add(video.token);
          // A listing that answered before this did may have added it as paused.
          updateRows(prev => prev
            .filter(r => r.localId === localId || r.token !== video.token)
            .map(r => (r.localId === localId ? { ...r, token: video.token, expiresAt: video.expires_at || null } : r)));
          rememberUpload(video.token, file, { source });
        }
      }));
    }
  }, [enqueue, updateRows]);

  /**
   * Carry on an open upload. An interrupted row resumes with the file this
   * tab still holds; a paused one needs `file` picked again, and refuses a
   * different one. → { success } | { success: false, code: 'need_file' | 'different_file' }
   */
  const resume = useCallback(async (localId, picked = null) => {
    const row = rowsRef.current.find(r => r.localId === localId);
    if (!row?.token || ACTIVE.includes(row.phase) || row.phase === 'elsewhere') return { success: false, code: 'not_resumable' };
    const file = picked || filesRef.current.get(localId);
    if (!file) return { success: false, code: 'need_file' };
    if (picked) {
      const upload = { declared_bytes: row.totalBytes };
      const fingerprint = await fileFingerprint(picked);
      if (!matchesPausedUpload(picked, upload, rememberedUpload(row.token), fingerprint)) {
        return { success: false, code: 'different_file' };
      }
      filesRef.current.set(localId, picked);
      if (!rememberedUpload(row.token)) rememberUpload(row.token, picked, { source: row.source });
    }
    patch(localId, { phase: 'waiting', code: null, name: row.name || file.name });
    enqueue(localId, (signal, onProgress) => resumeVideo(file, row.token, { signal, onProgress }));
    return { success: true };
  }, [enqueue, patch]);

  /**
   * Stop and delete an upload (in progress, interrupted or paused), or
   * dismiss a failed row. A paused row is looked at again first: since it was
   * listed, another device may have started sending it, or finished it — and
   * the DELETE would take that finished clip with it. When that look can't be
   * made (offline), nothing is deleted and the answer says so.
   * → { code: 'unknown' } when the paused row couldn't be checked, else undefined
   */
  const cancel = useCallback(async (localId) => {
    let row = rowsRef.current.find(r => r.localId === localId);
    if (row?.phase === 'paused' && row.token) {
      const seen = await recheck(localId);
      if (seen === null && rowsRef.current.some(r => r.localId === localId && r.phase === 'paused')) {
        return { code: 'unknown' };
      }
      if (seen !== 'paused') return undefined;
      row = rowsRef.current.find(r => r.localId === localId);
    }
    // Someone is sending it from another tab or device: not ours to stop.
    if (!row || row.phase === 'elsewhere') return undefined;
    const controller = controllersRef.current.get(localId);
    controller?.abort();
    removeRow(localId);
    // Open on the server and not being sent (or still waiting its turn):
    // the client never ran, so delete it here. A repeat DELETE is harmless.
    if (row?.token && row.phase !== 'failed') {
      VideoService.cancelUpload(row.token).then(() => refreshQuota());
    }
    if (row?.token) forgetUpload(row.token);
  }, [removeRow, refreshQuota, recheck]);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const value = useMemo(() => ({
    enabled, rows, quota, start, resume, cancel, subscribe, refreshQuota
  }), [enabled, rows, quota, start, resume, cancel, subscribe, refreshQuota]);

  return <VideoUploadsContext.Provider value={value}>{children}</VideoUploadsContext.Provider>;
}

const OFF = { enabled: false, rows: [], quota: null, start: () => {}, resume: async () => ({ success: false }), cancel: () => {}, subscribe: () => () => {}, refreshQuota: () => {} };

/**
 * The uploads belonging to one surface: a trip (the Recap composer), a stop
 * (the visit sheet, a review card), an album, or a place page (V7). Outside the provider (tests,
 * a page rendered alone) video is simply off.
 */
export function useVideoUploads({ tripId = null, activityId = null, albumId = null, attractionId = null } = {}) {
  const all = useContext(VideoUploadsContext) || OFF;
  const rows = useMemo(() => all.rows.filter(r => (
    (tripId == null || r.context?.trip_id === tripId)
    && (activityId == null || r.context?.activity_id === activityId)
    && (albumId == null || r.context?.album_id === albumId)
    // A place page's own uploads (V7): started there, no trip.
    && (attractionId == null || (r.context?.attraction_id === attractionId && !r.context?.trip_id))
  )), [all.rows, tripId, activityId, albumId, attractionId]);
  return { ...all, rows };
}

/** Call `handler(event)` when an upload finishes (see VideoUploadsProvider). */
export function useVideoUploadFinished(handler) {
  const { subscribe } = useContext(VideoUploadsContext) || OFF;
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; });
  useEffect(() => subscribe((event) => handlerRef.current?.(event)), [subscribe]);
}
